import React, { useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  FormControl,
  Text,
  FormLabel,
  Input,
  HStack,
  Textarea,
  Stack,
  Button,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import { LOCALE_REPLACEMENT_FOR_RECAPTCHA } from '@rsc/gatsby-localization-helpers';

export default function ContactForm({ brandEmail }) {
  const recaptchaRef = useRef();
  const { tt, locale } = useUiComponentsInternationalContext();
  const recaptchaLocale = LOCALE_REPLACEMENT_FOR_RECAPTCHA?.[locale] ?? locale;

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        firstName: yup
          .string()
          .required(
            tt(
              s => s.contactFormDeptComponent.firstNameRequiredValidationMessage
            )
          )
          .min(
            2,
            tt(s => s.contactFormDeptComponent.firstNameValidationMessage)
          ),
        lastName: yup.string().test(
          'len',
          tt(s => s.contactFormDeptComponent.lastNameValidationMessage),
          val => val.length === 0 || val.length > 1
        ),
        email: yup
          .string()
          .email(tt(s => s.contactFormDeptComponent.emailValidationMessage))
          .required(
            tt(s => s.contactFormDeptComponent.emailRequiredValidationMessage)
          ),
        outlet: yup.string().test(
          'len',
          tt(s => s.contactFormDeptComponent.outletNameValidationMessage),
          val => val.length === 0 || val.length > 1
        ),
        message: yup
          .string()
          .required(
            tt(s => s.contactFormDeptComponent.messageRequiredValidationMessage)
          )
          .min(
            15,
            tt(s => s.contactFormDeptComponent.messageLengthValidationMessage)
          ),
      }),
    [tt]
  );

  const {
    handleSubmit,
    register,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = data => {
    const { firstName, lastName, outlet, message } = data;

    const outletLabel = outlet ? `(${outlet})` : '';
    const subject = tt(s => s.contactFormDeptComponent.subjectText, {
      firstName,
      lastName,
      outletLabel,
    });

    const formattedMessage = message.replace(/\n/g, '%0d%0a');

    // TODO: Use a email service to send the email
    if (brandEmail) {
      const mLink = `mailto:${brandEmail}?subject=${subject}&body=${formattedMessage}`;
      window.open(mLink);
    }
  };

  const handleFormSubmit = async (data, event) => {
    event.preventDefault();
    const gRecaptchaResponse = await recaptchaRef.current.execute();

    if (!gRecaptchaResponse) return;

    onSubmit(data);
    reset();
  };

  return (
    <Box
      as="form"
      onSubmit={handleSubmit(handleFormSubmit)}
      w="full"
      rounded="xl"
      boxShadow="0px 4px 56px 0px rgba(5, 37, 77, 0.15)"
      p={6}
      bg="white"
    >
      <Stack spacing={4}>
        <HStack
          spacing={4}
          flexDirection={{ base: 'column', sm: 'row' }}
          align="flex-start"
        >
          <FormControl id="firstName" isRequired>
            <FormLabel>
              {tt(s => s.contactFormDeptComponent.firstNameLabel)}
            </FormLabel>
            <Input
              type="text"
              borderColor={
                errors.firstName ? 'alerts.danger.base' : 'neutral.200'
              }
              borderStyle="solid"
              borderWidth="1px"
              {...register('firstName')}
            />
            {errors.firstName && (
              <Text
                pos="relative"
                top="1"
                color="alerts.danger.base"
                textStyle="caption"
              >
                {errors.firstName.message}
              </Text>
            )}
          </FormControl>
          <FormControl id="lastName">
            <FormLabel>
              {tt(s => s.contactFormDeptComponent.lastNameLabel)}
            </FormLabel>
            <Input
              type="text"
              borderColor={
                errors.lastName ? 'alerts.danger.base' : 'neutral.200'
              }
              borderStyle="solid"
              borderWidth="1px"
              {...register('lastName')}
            />
            {errors.lastName && (
              <Text
                pos="relative"
                top="1"
                color="alerts.danger.base"
                textStyle="caption"
              >
                {errors.lastName.message}
              </Text>
            )}
          </FormControl>
        </HStack>
        <HStack
          spacing={4}
          flexDirection={{ base: 'column', sm: 'row' }}
          align="flex-start"
        >
          <FormControl id="email" isRequired>
            <FormLabel>
              {tt(s => s.contactFormDeptComponent.emailAddressLabel)}
            </FormLabel>
            <Input
              type="text"
              borderColor={errors.email ? 'alerts.danger.base' : 'neutral.200'}
              borderStyle="solid"
              borderWidth="1px"
              {...register('email')}
            />
            {errors.email && (
              <Text
                pos="relative"
                top="1"
                color="alerts.danger.base"
                textStyle="caption"
              >
                {errors.email.message}
              </Text>
            )}
          </FormControl>
          <FormControl id="outlet">
            <FormLabel>
              {tt(s => s.contactFormDeptComponent.outletNameLabel)}
            </FormLabel>
            <Input
              type="text"
              borderColor={errors.outlet ? 'alerts.danger.base' : 'neutral.200'}
              borderStyle="solid"
              borderWidth="1px"
              {...register('outlet')}
            />
            {errors.outlet && (
              <Text
                pos="relative"
                top="1"
                color="alerts.danger.base"
                textStyle="caption"
              >
                {errors.outlet.message}
              </Text>
            )}
          </FormControl>
        </HStack>
        <FormControl id="email" isRequired>
          <FormLabel>
            {tt(s => s.contactFormDeptComponent.messageLabel)}
          </FormLabel>
          <Textarea
            minH="8rem"
            resize="none"
            borderColor={errors.message ? 'alerts.danger.base' : 'neutral.200'}
            borderStyle="solid"
            borderWidth="1px"
            {...register('message')}
          />
          {errors.message && (
            <Text
              pos="relative"
              top="1"
              color="alerts.danger.base"
              textStyle="caption"
            >
              {errors.message.message}
            </Text>
          )}
        </FormControl>
        <Stack spacing={10} pt={2}>
          {process.env.GATSBY_PETSAFE_RECAPTCHA_SITE_KEY && (
            <Box display="none">
              <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                sitekey={process.env.GATSBY_PETSAFE_RECAPTCHA_SITE_KEY}
                hl={recaptchaLocale}
              />
            </Box>
          )}
          <Button
            variant="secondary"
            isLoading={isSubmitting}
            loadingText="Submitting"
            w="11rem"
            alignSelf="end"
            type="submit"
          >
            {tt(s => s.contactFormDeptComponent.submitButton)}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

ContactForm.propTypes = {
  brandEmail: PropTypes.string.isRequired,
};
